import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChatMessage, ChatState } from 'models/ai-chat/chat.types';
import { FeedbackRequest, QueryResponse } from 'models/ai-chat/ai-chat-response';
import { TableTransactionResponse } from 'models/ai-chat/table-pagination';

const initialState: ChatState = {
  messages: [],
  pendingMessage: false,
  error: null,
  currentConversationId: null,
  suggestions: [],
  suggestedVisualizations: [],
  suggestionText: null,
  suggestedQuestions: null,
  loading: false,
  tableRows: null,
  isDownloadingPDF: false,
  pdfDownloadError: null,
  pdfDownloadErrorMessageId: null
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    // Existing reducers
    setConversation: (state, action: PayloadAction<string | null>) => {
      state.currentConversationId = action.payload;
      state.messages = [];
      state.pendingMessage = false;
      state.error = null;
    },
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },
    clearMessages: (state) => {
      state.messages = [];
    },
    setSuggestions: (state, action: PayloadAction<string[]>) => {
      state.suggestions = action.payload;
    },

    // New methods to manually handle API states
    setPendingMessage: (state, action: PayloadAction<{ prompt: string; isEdit?: boolean; editMessageId?: string; isRegenerate?: boolean }>) => {
      state.pendingMessage = true;
      state.error = null;
      
      if (action.payload.isEdit && action.payload.editMessageId) {
        // For edit mode, find the user message and update its content
        const userMessageIndex = state.messages.findIndex(msg => msg.message_id === action.payload.editMessageId);
        if (userMessageIndex !== -1) {
          // Update the user message content with new prompt
          state.messages[userMessageIndex] = {
            ...state.messages[userMessageIndex],
            content: action.payload.prompt,
            is_edited: true
          };
          
          // Set the next message (assistant response) to pending
          const assistantMessageIndex = userMessageIndex + 1;
          if (assistantMessageIndex < state.messages.length) {
            state.messages[assistantMessageIndex] = {
              ...state.messages[assistantMessageIndex],
              content: '',
              pending: true,
              error: false,
              is_edited: true
            };
          }
        }
      } else if (action.payload.isRegenerate && action.payload.editMessageId) {
        // For regenerate mode, find the assistant message and set it to pending
        const assistantMessageIndex = state.messages.findIndex(msg => msg.message_id === action.payload.editMessageId);
        if (assistantMessageIndex !== -1) {
          state.messages[assistantMessageIndex] = {
            ...state.messages[assistantMessageIndex],
            content: '',
            pending: true,
            error: false,
            is_edited: true
          };
        }
      } else {
        // For new messages, add user message and pending assistant message
        state.messages.push({
          role: 'user',
          content: action.payload.prompt,
          created_at: new Date().toISOString(),
          message_id: `temp-${Date.now()}`,
          sql_query: '',
          feedback_type: null,
          feedback_value: null,
          metadata: null
        });
        
        // Add pending assistant message
        state.messages.push({
          role: 'assistant',
          content: '',
          created_at: new Date().toISOString(),
          message_id: `pending-${Date.now()}`,
          pending: true,
          sql_query:'',
          feedback_type: null,
          feedback_value: null,
          metadata: null
        });
      }
    },

    setMessageSuccess: (state, action: PayloadAction<QueryResponse>) => {
      state.pendingMessage = false;
      
      const response = action.payload;

      // Update conversation ID if not set
      if (!state.currentConversationId) {
        state.currentConversationId = response.conversation_id; 
      }

      // Check if this is an edit response (message already exists)
      // First try to find by exact message_id match
      let existingMessageIndex = state.messages.findIndex(msg => msg.message_id === response.message_id);
      const existingParentMessageIndex = state.messages.findIndex(msg => msg.role === 'user' && msg.message_id.includes('temp'));

      if (existingMessageIndex === -1) {
        existingMessageIndex = state.messages.findIndex(msg => msg.pending === true && msg.role === 'assistant');
      }

      if(existingParentMessageIndex !== -1 && response.parent_message_id){
        const existingMessage = state.messages[existingParentMessageIndex];
          state.messages[existingParentMessageIndex] = {
            ...existingMessage,
            message_id: response.parent_message_id
          };
      }

      
      if (response.error) {
        state.error = response.error;
        const errorMessage = {
          role: 'assistant' as const,
          content: `Please contact AJ for further assistance`,
          created_at: new Date().toISOString(),
          message_id: response.message_id,
          sql_query: response.sql_query,
          visualization: {
            type: 'text',
            data: null
          },
          error: true,
          feedback_type: null,
          feedback_value: null,
          metadata: response.metadata
        };

        if (existingMessageIndex !== -1) {
          // Replace existing message
          state.messages[existingMessageIndex] = errorMessage;
        } else {
          // Add new message
          state.messages.push(errorMessage);
        }
        
        state.suggestions = [];
        state.suggestedQuestions = [];
        state.suggestedVisualizations = [];
      } else {
        // Map insights_data array to insights object with proper null/undefined handling
        let insights: { summary_insights?: string; dimension_insights?: string; time_insights?: string; report_insights?: any[] } | undefined = undefined;
        
        if (response?.insights_data && Array.isArray(response.insights_data) && response.insights_data.length > 0) {
          insights = {
            summary_insights: undefined,
            dimension_insights: undefined,
            time_insights: undefined,
            report_insights: undefined
          };
          
          response.insights_data.forEach(insight => {
            // Check if insight object and properties exist
            if (insight && insight.insight_type) {
              switch (insight.insight_type) {
                case 'summary_insights':
                  if (insight.insights) {
                    insights!.summary_insights = insight.insights;
                  }
                  break;
                case 'dimension_insights':
                  if (insight.insights) {
                    insights!.dimension_insights = insight.insights;
                  }
                  break;
                case 'time_insights':
                  if (insight.insights) {
                    insights!.time_insights = insight.insights;
                  }
                  break;
                case 'report_insights':
                  if (insight.report_data && insight.report_data.length > 0) {
                    insights!.report_insights = insight.report_data;
                  }
                  break;
                default:
                  console.warn('Unknown insight type:', insight.insight_type);
              }
            } else {
              console.warn('Invalid insight object:', insight);
            }
          });
        }

        const assistantMessage = {
          role: 'assistant' as const,
          content: response.text,
          created_at: new Date().toISOString(),
          message_id: response.message_id,
          sql_query: response.sql_query,
          visualization: {
            type: response.visualization,
            data: response.data,
          },
          feedback_type: null,
          feedback_value: null,
          metadata: response.metadata,
          insights: insights
        };

        if (existingMessageIndex !== -1) {
          // Replace existing message (for edits) - preserve existing feedback
          const existingMessage = state.messages[existingMessageIndex];
          state.messages[existingMessageIndex] = {
            ...assistantMessage,
            feedback_type: existingMessage.feedback_type,
            feedback_value: existingMessage.feedback_value
          };
        } else {
          // Add new message (for new queries)
          state.messages.push(assistantMessage);
        }
        
        // Process suggestions
        if (response.suggestiontext) {
          const suggestions = response.suggestiontext
            .split('?')
            .map((s:any) => s.trim())
            .filter((s:any) => s.length > 0)
            .map((s:any) => s + '?');
          
          state.suggestions = suggestions;
        }

        // Process suggested questions
        if (response.suggested_questions && response.suggested_questions.length > 0) {
          state.suggestedQuestions = state.suggestions.concat(response.suggested_questions);
        }

        // Process suggested visualizations
        if (response.suggested_visualizations && response.suggested_visualizations.length > 0) {
          state.suggestedVisualizations = response.suggested_visualizations;
        }
      }
    },


    loadExistingMessageSuccess: (state, action: PayloadAction<QueryResponse[]>) => {
      state.pendingMessage = false;
    
      state.messages = state.messages.filter((msg:any) => !msg.pending);
      const response = action.payload;

      if(response!=null && response.length > 0){
        response.forEach(element => {
          // Map insights_data array to insights object with proper null/undefined handling
          let insights: { summary_insights?: string; dimension_insights?: string; time_insights?: string; report_insights?: any[] } | undefined = undefined;

          if (element?.insights_data && Array.isArray(element.insights_data) && element.insights_data.length > 0) {
            insights = {
              summary_insights: undefined,
              dimension_insights: undefined,
              time_insights: undefined,
              report_insights: undefined
            };
            
            element.insights_data.forEach(insight => {
              // Check if insight object and properties exist
              if (insight && insight.insight_type) {
                switch (insight.insight_type) {
                  case 'summary_insights':
                    if (insight.insights) {
                      insights!.summary_insights = insight.insights;
                    }
                    break;
                  case 'dimension_insights':
                    if (insight.insights) {
                      insights!.dimension_insights = insight.insights;
                    }
                    break;
                  case 'time_insights':
                    if (insight.insights) {
                      insights!.time_insights = insight.insights;
                    }
                    break;
                  case 'report_insights':
                    if (insight.report_data && insight.report_data.length > 0) {
                      insights!.report_insights = insight.report_data;
                    }
                    break;
                  default:
                    console.warn('Unknown insight type:', insight.insight_type);
                }
              } else {
                console.warn('Invalid insight object:', insight);
              }
            });
          }

          state.messages.push({
            role: (element?.role === 'user') ? 'user' : 'assistant',
            content: element?.text || '',
            created_at: new Date().toISOString(),
            message_id: element?.message_id || '',
            sql_query: element?.sql_query || '',
            visualization: {
              type: element?.visualization || 'text',
              data: (element?.data && element.data !== null && element.data !== '') ? 
                (() => {
                  try {
                    return JSON.parse(element.data);
                  } catch (error) {
                    console.warn('Failed to parse visualization data:', error);
                    return null;
                  }
                })() : null,
            },
            feedback_type: (element?.feedback?.type) ? element.feedback.type : null,
            feedback_value: (element?.feedback?.value) ? element.feedback.value : null,
            metadata: element?.metadata || null,
            insights: insights
          });
        });
      }

    },

    setMessageFailure: (state, action: PayloadAction<string>) => {
      state.pendingMessage = false;
      state.error = action.payload || 'Failed to send message';
      
      // Remove pending messages
      state.messages = state.messages.filter((msg:any) => !msg.pending);

      // Check if we already have an error message for this same error to prevent duplicates
      const existingError = state.messages.find((msg: any) => 
        msg.error && msg.content === state.error && 
        Date.now() - new Date(msg.created_at).getTime() < 5000 // Within last 5 seconds
      );

      // Only add error message if we don't have a recent duplicate
      if (!existingError) {
        state.messages.push({
          role: 'assistant',
          content: state.error,
          created_at: new Date().toISOString(),
          message_id: `error-${Date.now()}`,
          error: true,
          sql_query: '',
          feedback_type: null,
          feedback_value: null,
          metadata: null
        });
      }
    },

    addMessageFeedback: (state, action: PayloadAction<FeedbackRequest>) => {
      const { message_id, feedback_type, feedback_value } = action.payload;
      
      const messageIndex = state.messages.findIndex(
        msg => msg.message_id === message_id
      );

      if (messageIndex !== -1) {
        state.messages[messageIndex] = {
          ...state.messages[messageIndex],
          feedback_type: feedback_type,
          feedback_value:feedback_value
        };
      }
    },

    removeMessageFeedback: (state, action: PayloadAction<FeedbackRequest>) => {
      const { message_id } = action.payload;
      
      const messageIndex = state.messages.findIndex(
        msg => msg.message_id === message_id
      );

      if (messageIndex !== -1) {
        state.messages[messageIndex] = {
          ...state.messages[messageIndex],
          feedback_type: null,
          feedback_value:null
        };
      }
    },
    setTablePaginatedDataSet:(state, action: PayloadAction<TableTransactionResponse>) => {
      state.tableRows = action.payload;
    },

    /**
     * Updates a message with new data from filter updates
     * @param state - The current chat state
     * @param action - Action containing messageId and newData
     */
    updateMessageData: (state, action: PayloadAction<{ messageId: string; newData: any }>) => {
      const { messageId, newData } = action.payload;
      
      const messageIndex = state.messages.findIndex(
        msg => msg.message_id === messageId
      );

      if (messageIndex !== -1 ) {
        // Check if this is a full message update (for editing) or just data update
        if (newData.text !== undefined) {
          // Full message update for editing
          state.messages[messageIndex] = {
            ...state.messages[messageIndex],
            content: newData.text,
            sql_query: newData.sql_query,
            metadata: newData.metadata,
            visualization: {
              type: newData.visualization || 'text',
              data: newData.data
            },
            pending: false,
            error: false,
            insights: undefined // Clear insights when editing
          };
        } else {
          // Partial data update for filters
          if (!state.messages[messageIndex].visualization) {
            state.messages[messageIndex].visualization = { type: 'table', data: null };
          }
          
          if(newData.data == null){
            state.messages[messageIndex].visualization!.data = null;
            state.messages[messageIndex].sql_query = newData.sql_query;
            state.messages[messageIndex].filter_text = newData.text;

          }else{
            state.messages[messageIndex].visualization!.data = newData.data;
            state.messages[messageIndex].visualization!.type = newData.visualization || state.messages[messageIndex].visualization!.type;
            state.messages[messageIndex].sql_query = newData.sql_query;
            state.messages[messageIndex].metadata = newData.metadata;
            
            // Update insights if provided
            if (newData.insights) {
              state.messages[messageIndex].insights = newData.insights;
            }
          }
        }
      }
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setPdfDownloadLoading: (state, action: PayloadAction<boolean>) => {
      state.isDownloadingPDF = action.payload;
      if (action.payload) {
        state.pdfDownloadError = null;
        state.pdfDownloadErrorMessageId = null;
      }
    },

    setPdfDownloadError: (state, action: PayloadAction<{ error: string | null; messageId: string | null }>) => {
      state.pdfDownloadError = action.payload.error;
      state.pdfDownloadErrorMessageId = action.payload.messageId;
      state.isDownloadingPDF = false;
    },
    addInsightToMessage: (state, action: PayloadAction<{ messageId: string; insightType: string; insight: string, report_data:any[] }>) => {
      const { messageId, insightType, insight } = action.payload;
      const messageIndex = state.messages.findIndex(msg => msg.message_id === messageId);
      
      if (messageIndex !== -1) {
        // Initialize insights object if it doesn't exist
        if (!state.messages[messageIndex].insights) {
          state.messages[messageIndex].insights = {};
        }

        if(action.payload.report_data && action.payload.report_data.length > 0){
          state.messages[messageIndex].insights!.report_insights = action.payload.report_data;
        }
        
        // Add the insight based on type
        if (insightType === 'summary_insights') {
          state.messages[messageIndex].insights!.summary_insights = insight;
        } else if (insightType === 'dimension_insights') {
          state.messages[messageIndex].insights!.dimension_insights = insight;
        } else if (insightType === 'time_insights') {
          state.messages[messageIndex].insights!.time_insights = insight;
        }
      }
    },

  }
});

export const { 
  setConversation, 
  addMessage, 
  clearMessages, 
  setSuggestions,
  setPendingMessage,
  setMessageSuccess,
  loadExistingMessageSuccess,
  setMessageFailure,
  addMessageFeedback,
  removeMessageFeedback,
  setTablePaginatedDataSet,
  setPdfDownloadLoading,
  setPdfDownloadError,
  updateMessageData,
  setLoading,
  addInsightToMessage
} = chatSlice.actions;
export default chatSlice.reducer;